import {
  buildClipPolygonsCacheKey,
  clipCanvasToTalhoes,
  ClipPolygon,
} from "./clipMask";
import { buildBandColorLookup, sampleRainfallBandColor } from "./color";
import {
  DEFAULT_EDGE_FEATHER_RATIO,
  DEFAULT_IDW_POWER,
  DEFAULT_SURFACE_BLUR_PX,
  DEFAULT_SURFACE_OPACITY,
  DEFAULT_SURFACE_WIDTH,
  DEFAULT_VALUE_BLUR_RADIUS,
} from "./constants";
import { estimateMaxDistanceDegrees } from "./idw";
import { yieldToMain } from "./schedule";
import {
  buildCurvedRainfallValues,
  buildCurvedRainfallValuesAsync,
} from "./surfaceInterpolation";
import {
  buildRainfallSurfaceCacheKey,
  getCachedRainfallSurface,
  setCachedRainfallSurface,
} from "./surfaceCache";
import { RainfallBand, RenderRainfallSurfaceOptions } from "./types";

export interface RainfallSurfaceResult {
  url: string;
  width: number;
  height: number;
}

function stationsCacheKey(
  stations: RenderRainfallSurfaceOptions["stations"],
): string {
  return stations
    .map(
      (station) =>
        `${station.id}:${station.lat.toFixed(5)}:${station.lng.toFixed(
          5,
        )}:${station.precipitacaoMm}`,
    )
    .join(";");
}

function bandsCacheKey(bands: RainfallBand[]): string {
  return bands
    .map((band) => `${band.codigo}:${band.minMm}:${band.maxMm}:${band.color}`)
    .join(";");
}

function resolveRenderSize(
  bounds: RenderRainfallSurfaceOptions["bounds"],
  width: number,
  requestedHeight?: number,
): { width: number; height: number } | null {
  if (
    !bounds ||
    width <= 0 ||
    (requestedHeight !== undefined && requestedHeight <= 0)
  ) {
    return null;
  }

  if (
    !(
      Number.isFinite(bounds.south) &&
      Number.isFinite(bounds.north) &&
      Number.isFinite(bounds.west) &&
      Number.isFinite(bounds.east)
    ) ||
    bounds.north <= bounds.south ||
    bounds.east <= bounds.west
  ) {
    return null;
  }

  const physicalWidthKm =
    (bounds.east - bounds.west) *
    111.32 *
    Math.cos((((bounds.south + bounds.north) / 2) * Math.PI) / 180);
  const physicalHeightKm = (bounds.north - bounds.south) * 110.574;
  const height =
    requestedHeight ??
    Math.max(300, Math.round(width * (physicalHeightKm / physicalWidthKm)));

  return { width, height };
}

function exportBlurredSurface(
  source: HTMLCanvasElement,
  blurPx: number,
): HTMLCanvasElement {
  if (!(blurPx > 0)) {
    return source;
  }

  const blurOnce = (input: HTMLCanvasElement, amount: number) => {
    const blurred = document.createElement("canvas");
    blurred.width = input.width;
    blurred.height = input.height;
    const blurCtx = blurred.getContext("2d");
    if (!blurCtx) {
      return input;
    }
    blurCtx.clearRect(0, 0, blurred.width, blurred.height);
    blurCtx.filter = `blur(${amount}px)`;
    blurCtx.drawImage(input, 0, 0);
    blurCtx.filter = "none";
    return blurred;
  };

  let current = blurOnce(source, blurPx);
  if (blurPx >= 2) {
    current = blurOnce(current, Math.max(blurPx * 0.5, 1.5));
  }
  return current;
}

function paintSurfaceCanvas(options: {
  values: Float32Array;
  width: number;
  height: number;
  bands: RainfallBand[];
  selectedBandCodes: number[];
  opacity: number;
  blurPx: number;
  clipRings: ClipPolygon[];
  bounds: RenderRainfallSurfaceOptions["bounds"];
}): RainfallSurfaceResult | null {
  const {
    values,
    width,
    height,
    bands,
    selectedBandCodes,
    opacity,
    blurPx,
    clipRings,
    bounds,
  } = options;

  let canvas: HTMLCanvasElement;
  try {
    canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
  } catch (error) {
    console.error("[rainfall-interpolation] Failed to create canvas.", error);
    return null;
  }

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    console.error("[rainfall-interpolation] 2D canvas context unavailable.");
    return null;
  }

  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;
  const colorByBand = buildBandColorLookup(bands, selectedBandCodes, opacity);

  for (let i = 0; i < values.length; i += 1) {
    const offset = i * 4;
    const valueMm = values[i];
    if (!Number.isFinite(valueMm)) {
      data[offset + 3] = 0;
      continue;
    }

    const color = sampleRainfallBandColor(
      valueMm,
      bands,
      selectedBandCodes,
      opacity,
      colorByBand,
    );
    if (!color) {
      data[offset + 3] = 0;
      continue;
    }

    data[offset] = color.r;
    data[offset + 1] = color.g;
    data[offset + 2] = color.b;
    data[offset + 3] = color.a;
  }

  try {
    ctx.putImageData(imageData, 0, 0);
    const blurred = exportBlurredSurface(canvas, blurPx);
    const clipped = clipCanvasToTalhoes(blurred, clipRings, bounds);
    return {
      url: clipped.toDataURL("image/png"),
      width,
      height,
    };
  } catch (error) {
    console.error(
      "[rainfall-interpolation] Failed to export surface PNG.",
      error,
    );
    return null;
  }
}

function buildCacheKey(options: {
  stations: RenderRainfallSurfaceOptions["stations"];
  bands: RainfallBand[];
  selectedBandCodes: number[];
  bounds: RenderRainfallSurfaceOptions["bounds"];
  clipRings: ClipPolygon[];
  width: number;
  height: number;
  power: number;
  opacity: number;
  maxDistanceDegrees: number;
  edgeFeatherRatio: number;
  blurPx: number;
  valueBlurRadius: number;
}): string {
  return buildRainfallSurfaceCacheKey({
    stationsKey: `thin-plate-spline-v1:${stationsCacheKey(options.stations)}`,
    bandsKey: bandsCacheKey(options.bands),
    selectedCodesKey: [...options.selectedBandCodes]
      .sort((a, b) => a - b)
      .join(","),
    boundsKey: `${options.bounds.south}:${options.bounds.west}:${options.bounds.north}:${options.bounds.east}`,
    clipKey: buildClipPolygonsCacheKey(options.clipRings),
    width: options.width,
    height: options.height,
    power: options.power,
    opacity: options.opacity,
    maxDistanceDegrees: options.maxDistanceDegrees,
    edgeFeatherRatio: options.edgeFeatherRatio,
    blurPx: options.blurPx,
    valueBlurRadius: options.valueBlurRadius,
  });
}

export function renderRainfallSurface(
  options: RenderRainfallSurfaceOptions,
): RainfallSurfaceResult | null {
  const {
    stations,
    bands,
    selectedBandCodes,
    bounds,
    clipPolygons,
    width = DEFAULT_SURFACE_WIDTH,
    height: requestedHeight,
    power = DEFAULT_IDW_POWER,
    opacity = DEFAULT_SURFACE_OPACITY,
    edgeFeatherRatio = DEFAULT_EDGE_FEATHER_RATIO,
    blurPx = DEFAULT_SURFACE_BLUR_PX,
    valueBlurRadius = DEFAULT_VALUE_BLUR_RADIUS,
  } = options;

  const clipRings = (clipPolygons ?? []) as ClipPolygon[];

  if (!stations.length || !bands.length || !selectedBandCodes.length) {
    return null;
  }

  const size = resolveRenderSize(bounds, width, requestedHeight);
  if (!size) {
    console.warn(
      "[rainfall-interpolation] Invalid bounds; skipping surface render.",
    );
    return null;
  }

  const maxDistance =
    options.maxDistanceDegrees ?? estimateMaxDistanceDegrees(stations, bounds);
  const cacheKey = buildCacheKey({
    stations,
    bands,
    selectedBandCodes,
    bounds,
    clipRings,
    width: size.width,
    height: size.height,
    power,
    opacity,
    maxDistanceDegrees: maxDistance,
    edgeFeatherRatio,
    blurPx,
    valueBlurRadius,
  });

  const cached = getCachedRainfallSurface(cacheKey);
  if (cached) {
    return cached;
  }

  if (typeof document === "undefined") {
    console.warn(
      "[rainfall-interpolation] document unavailable; cannot render canvas.",
    );
    return null;
  }

  const values = buildCurvedRainfallValues({
    stations,
    polygons: clipRings,
    bounds,
    width: size.width,
    height: size.height,
    idwPower: power,
    smoothingSigma: valueBlurRadius,
  });

  const result = paintSurfaceCanvas({
    values,
    width: size.width,
    height: size.height,
    bands,
    selectedBandCodes,
    opacity,
    blurPx,
    clipRings,
    bounds,
  });
  if (!result) {
    return null;
  }
  setCachedRainfallSurface(cacheKey, result);
  return result;
}

export async function renderRainfallSurfaceAsync(
  options: RenderRainfallSurfaceOptions & {
    shouldCancel?: () => boolean;
  },
): Promise<RainfallSurfaceResult | null> {
  const {
    stations,
    bands,
    selectedBandCodes,
    bounds,
    clipPolygons,
    width = DEFAULT_SURFACE_WIDTH,
    height: requestedHeight,
    power = DEFAULT_IDW_POWER,
    opacity = DEFAULT_SURFACE_OPACITY,
    edgeFeatherRatio = DEFAULT_EDGE_FEATHER_RATIO,
    blurPx = DEFAULT_SURFACE_BLUR_PX,
    valueBlurRadius = DEFAULT_VALUE_BLUR_RADIUS,
    shouldCancel,
  } = options;

  const clipRings = (clipPolygons ?? []) as ClipPolygon[];

  if (!stations.length || !bands.length || !selectedBandCodes.length) {
    return null;
  }

  const size = resolveRenderSize(bounds, width, requestedHeight);
  if (!size) {
    console.warn(
      "[rainfall-interpolation] Invalid bounds; skipping surface render.",
    );
    return null;
  }

  if (shouldCancel?.()) {
    return null;
  }

  const maxDistance =
    options.maxDistanceDegrees ?? estimateMaxDistanceDegrees(stations, bounds);
  const cacheKey = buildCacheKey({
    stations,
    bands,
    selectedBandCodes,
    bounds,
    clipRings,
    width: size.width,
    height: size.height,
    power,
    opacity,
    maxDistanceDegrees: maxDistance,
    edgeFeatherRatio,
    blurPx,
    valueBlurRadius,
  });

  const cached = getCachedRainfallSurface(cacheKey);
  if (cached) {
    return cached;
  }

  if (typeof document === "undefined") {
    console.warn(
      "[rainfall-interpolation] document unavailable; cannot render canvas.",
    );
    return null;
  }

  const values = await buildCurvedRainfallValuesAsync({
    stations,
    polygons: clipRings,
    bounds,
    width: size.width,
    height: size.height,
    idwPower: power,
    smoothingSigma: valueBlurRadius,
    shouldCancel,
  });

  if (!values || shouldCancel?.()) {
    return null;
  }

  await yieldToMain();
  if (shouldCancel?.()) {
    return null;
  }

  const result = paintSurfaceCanvas({
    values,
    width: size.width,
    height: size.height,
    bands,
    selectedBandCodes,
    opacity,
    blurPx,
    clipRings,
    bounds,
  });
  if (!result || shouldCancel?.()) {
    return null;
  }

  setCachedRainfallSurface(cacheKey, result);
  return result;
}
